import { TripProgress } from "@/components/trip-progress";
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogHeader,
  Text,
  TextArea,
  TextInput,
  useToast,
  VStack,
} from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Camera,
  ExternalLink,
  MapPin,
  PauseCircle,
  Phone,
  Play,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { CompleteTripButton } from "@/components/complete-trip-button";
import { AppShell } from "@/components/app-shell";
import { FullScreenLoader } from "@/components/spinner";
import { api } from "@/lib/api";
import { formatTripLabel } from "@/lib/trip-label";
import { prepareProofPhoto } from "@/lib/proof-photo";
import type { Task, Trip } from "@/types/api";
import type { BadgeVariant } from "@/types/ui";
import {
  cancellationHoldError,
  cancellationReviewCopy,
  isTaskHeld,
} from "@/lib/work-hold";

// Tasks live under trip.waypoints[].tasks[] — the top-level trip.tasks select
// has no address. Keep waypoint order while preserving each task identity.
function tripTasks(trip: Trip): Task[] {
  return (trip.waypoints ?? [])
    .slice()
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .flatMap((w) => w.tasks ?? []);
}

// The server pre-joins the address into customerAddress; fall back to the
// structured address. There are no lat/lng, so Maps links search by text.
function addressText(t: Task): string {
  if (t.operationalUpdate?.changedFields.includes("unit") && t.address) {
    const a = t.address;
    return [
      a.line1,
      t.operationalUpdate.current.unit,
      a.city,
      a.province,
      a.postalCode,
    ]
      .filter(Boolean)
      .join(", ");
  }
  if (t.customerAddress) return t.customerAddress;
  const a = t.address;
  return a
    ? [a.line1, a.line2, a.city, a.province, a.postalCode]
        .filter(Boolean)
        .join(", ")
    : "";
}

function mapsHref(t: Task): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText(t))}`;
}

const TASK_STATUS_VARIANT: Record<string, BadgeVariant> = {
  COMPLETED: "success",
  FAILED: "error",
  IN_PROGRESS: "info",
  ASSIGNED: "info",
};

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED", "CANCELLED"]);

const styles = stylex.create({
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 12,
    fontSize: "0.875rem",
    color: "var(--color-text-secondary)",
    textDecoration: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  cardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  cardLeft: {
    minWidth: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  seqRow: { display: "flex", alignItems: "center", gap: 8 },
  shipmentReference: {
    fontSize: "1rem",
    fontWeight: 600,
    overflowWrap: "anywhere",
  },
  name: {
    overflowWrap: "anywhere",
    whiteSpace: "normal",
    fontWeight: 500,
  },
  longText: { minWidth: 0, overflowWrap: "anywhere", whiteSpace: "normal" },
  senderRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: 6,
    marginTop: 4,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  senderLabel: { flexShrink: 0 },
  senderValue: {
    flexBasis: "10rem",
    flexGrow: 1,
    minWidth: 0,
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },
  taskStatus: { alignSelf: "flex-start", marginTop: 4 },
  contactRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    flexWrap: "wrap",
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  callLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "var(--color-text-primary)",
    fontSize: "0.875rem",
  },
  mapLink: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    color: "var(--color-text-secondary)",
    textDecoration: "none",
  },
  actions: {
    marginTop: 12,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  heldCard: {
    borderColor: "var(--color-border)",
    backgroundColor: "var(--color-background-muted)",
  },
  holdNotice: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    border: "1px solid var(--color-border)",
    color: "var(--color-text-secondary)",
  },
  operationalNotice: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    border: "1px solid var(--color-border)",
    backgroundColor: "var(--color-background-muted)",
  },
  operationalRows: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    margin: 0,
    marginTop: 12,
  },
  operationalField: { minWidth: 0, overflowWrap: "anywhere" },
  operationalValues: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
    margin: 0,
    marginTop: 8,
  },
  operationalValue: { margin: 0, whiteSpace: "pre-wrap" },
  reviewStatus: {
    marginTop: 12,
  },
  holdText: {
    display: "flex",
    minWidth: 0,
    flexDirection: "column",
    gap: 2,
  },
  holdSummary: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "var(--color-background-muted)",
  },
  errorNotice: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: "var(--color-background-muted)",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    paddingBlock: 8,
  },
  photoSection: { display: "flex", flexDirection: "column", gap: 8 },
  preview: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 8,
    border: "1px solid var(--color-border)",
  },
  previewImg: {
    display: "block",
    maxHeight: 224,
    width: "100%",
    objectFit: "cover",
  },
  removeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    display: "flex",
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
  },
  hiddenInput: { display: "none" },
  dialogFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
});

export function TripDetailScreen() {
  const { tripId } = useParams<{ tripId: string }>();
  const toast = useToast();

  const { data: trip, isLoading } = useQuery({
    queryKey: ["trip", tripId],
    queryFn: async () => {
      const res = await api.get<Trip>(`/api/v2/trips/${tripId}`);
      return res.data;
    },
    enabled: !!tripId,
    refetchInterval: 30_000,
  });

  const qc = useQueryClient();
  const startTrip = useMutation({
    mutationFn: () => api.patch(`/api/v2/trips/${tripId}/start`),
    onSuccess: () => {
      toast({ body: "Trip started" });
      void qc.invalidateQueries({ queryKey: ["trip", tripId] });
      void qc.invalidateQueries({ queryKey: ["operator-trips"] });
    },
    onError: (error: unknown) => {
      if (cancellationHoldError(error).isHold) {
        toast({
          body: "Trip has work under cancellation review. Refresh and continue only unrelated work.",
          type: "error",
        });
        void qc.invalidateQueries({ queryKey: ["trip", tripId] });
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ body: `Couldn't start trip: ${message}`, type: "error" });
    },
  });

  if (isLoading) return <FullScreenLoader label="Loading trip…" />;
  if (!trip) {
    return (
      <AppShell>
        <Text size="sm" color="secondary">
          Trip not found.
        </Text>
      </AppShell>
    );
  }

  const tasks = tripTasks(trip);
  const heldTaskCount = tasks.filter((task) => isTaskHeld(task)).length;
  const actionableTaskCount = tasks.length - heldTaskCount;
  const canStart = trip.status === "PLANNED" && actionableTaskCount > 0;
  const name = formatTripLabel(trip);

  return (
    <AppShell>
      <Link to="/" {...stylex.props(styles.backLink)}>
        <ArrowLeft size={16} /> My trips
      </Link>

      <div {...stylex.props(styles.header)}>
        <VStack gap={1} align="start">
          <Text as="h1" size="lg" weight="semibold">
            {name}
          </Text>
          <Text size="xsm" color="secondary">
            {trip.status}
          </Text>
          <TripProgress trip={trip} />
        </VStack>
        <CompleteTripButton trip={trip} />
        {trip.status === "PLANNED" ? (
          <Link to={`/trips/${trip.id}/collection`}>Collection checklist</Link>
        ) : null}
        {canStart ? (
          <Button
            variant="primary"
            isLoading={startTrip.isPending}
            icon={<Play size={16} />}
            label="Start trip"
            onClick={() => startTrip.mutate()}
          />
        ) : null}
      </div>

      {heldTaskCount > 0 ? (
        <div role="status" {...stylex.props(styles.holdSummary)}>
          <Text size="sm" weight="semibold">
            {heldTaskCount} task{heldTaskCount === 1 ? "" : "s"} under
            cancellation review
          </Text>
          <Text size="sm" color="secondary">
            Held work is not cancelled. Continue unrelated tasks and follow the
            instructions on each held task.
          </Text>
        </div>
      ) : null}

      <ol {...stylex.props(styles.list)}>
        {tasks.map((task) => (
          <li key={task.id}>
            <TaskRow task={task} tripId={tripId} tripStatus={trip.status} />
          </li>
        ))}
      </ol>
    </AppShell>
  );
}

function TaskRow({
  task,
  tripId,
  tripStatus,
}: {
  task: Task;
  tripId: string | undefined;
  tripStatus: Trip["status"];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [failOpen, setFailOpen] = useState(false);

  const [proofFile, setProofFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [completionError, setCompletionError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const held = isTaskHeld(task);
  const needsDeliveryStart =
    task.type === "CUSTOMER_DELIVERY" && task.status === "PENDING";

  // Object-URL preview for the chosen proof photo (revoked on change/unmount).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!proofFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(proofFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [proofFile]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["trip", tripId] });
    void qc.invalidateQueries({ queryKey: ["operator-trips"] });
  };

  const resetComplete = () => {
    setProofFile(null);
    setNotes("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startDelivery = useMutation({
    mutationFn: () => api.post(`/api/v2/tasks/${task.id}/start-delivery`),
    onSuccess: () => {
      toast({ body: "Delivery started" });
      invalidate();
    },
    onError: (error: unknown) => {
      if (cancellationHoldError(error).isHold) {
        toast({ body: "Task is under cancellation review", type: "error" });
        invalidate();
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ body: `Couldn't start delivery: ${message}`, type: "error" });
    },
  });

  const completeTask = useMutation({
    mutationFn: async () => {
      if (needsDeliveryStart)
        throw new Error("Start delivery before completing it");
      if (!proofFile) throw new Error("Attach a proof photo first");

      const preparationStartedAt = performance.now();
      const { data, mimeType } = await prepareProofPhoto(proofFile);
      const prepareMs = performance.now() - preparationStartedAt;

      // /complete persists the proof from inline media (createProofWithMedia on
      // the server) — no separate address-media upload. Body matches the
      // server mediaSchema: { data: base64, type: "photo"|"video", mimeType }.
      const requestStartedAt = performance.now();
      const response = await api.patch(`/api/v2/tasks/${task.id}/complete`, {
        media: [{ data, type: "photo", mimeType }],
        notes: notes.trim() || undefined,
      });
      console.info("Proof completion timing", {
        inputBytes: proofFile.size,
        encodedChars: data.length,
        prepareMs: Math.round(prepareMs),
        requestMs: Math.round(performance.now() - requestStartedAt),
        serverTiming: response.headers["server-timing"] ?? null,
      });
      return response;
    },
    onSuccess: () => {
      toast({ body: "Task completed" });
      setCompleteOpen(false);
      resetComplete();
      setCompletionError(null);
      invalidate();
    },
    onError: (error: unknown) => {
      if (cancellationHoldError(error).isHold) {
        setCompletionError(cancellationReviewCopy(task.status));
        toast({ body: "Task is under cancellation review", type: "error" });
        invalidate();
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ body: `Couldn't complete task: ${message}`, type: "error" });
    },
  });

  const failTask = useMutation({
    mutationFn: () => {
      const trimmed = reason.trim();
      if (!trimmed) throw new Error("A reason is required");
      // Mirror the native fail payload: { reason, reasonCode }. 9 = generic.
      return api.patch(`/api/v2/tasks/${task.id}/fail`, {
        reason: trimmed,
        reasonCode: 9,
      });
    },
    onSuccess: () => {
      toast({ body: "Task marked failed" });
      setFailOpen(false);
      setReason("");
      invalidate();
    },
    onError: (e: Error) =>
      toast({ body: `Couldn't fail task: ${e.message}`, type: "error" }),
  });

  const acknowledgeUpdate = useMutation({
    mutationFn: () =>
      api.post(`/api/v2/tasks/${task.id}/acknowledge-operational-update`, {
        fingerprint: task.operationalUpdate?.fingerprint,
      }),
    onSuccess: () => {
      toast({ body: "Updated details reviewed" });
      invalidate();
    },
    onError: () =>
      toast({ body: "Could not save your confirmation", type: "error" }),
  });

  const done = TERMINAL_STATUSES.has(task.status);
  const number = task.shipment?.shipmentNumber;
  const shipmentReference =
    number != null && number > 0
      ? `SHP-${number}`
      : "Shipment reference unavailable";
  const taskReference =
    task.taskNumber != null && task.taskNumber > 0
      ? `TSK-${task.taskNumber}`
      : "Task reference unavailable";
  const taskType = task.type.replace(/_/g, " ").toLowerCase();
  const actionIdentity = (
    <div {...stylex.props(styles.cardLeft)}>
      <div {...stylex.props(styles.shipmentReference)}>{shipmentReference}</div>
      <Text size="sm" color="secondary">
        {taskReference} · {taskType}
      </Text>
    </div>
  );
  const operationalUpdate = task.operationalUpdate;
  const taskInstructions =
    task.type === "CUSTOMER_DELIVERY" ? task.notes : task.addressInstructions;
  const currentName = operationalUpdate?.changedFields.includes("contactName")
    ? operationalUpdate.current.contactName
    : task.contactName;
  const senderCompany = task.shipmentSender?.company?.trim();
  const senderName = task.shipmentSender?.name?.trim();
  const senderDisplay =
    [senderCompany, senderName !== senderCompany ? senderName : null]
      .filter(Boolean)
      .join(" · ") || "Sender unavailable";
  const contactPhone = (
    operationalUpdate?.changedFields.includes("contactPhone")
      ? operationalUpdate.current.contactPhone
      : task.contactPhone
  )?.trim();
  const dialNumber = contactPhone?.replace(/[^\d+]/g, "");
  const operationalLabels = {
    unit: "Unit / apartment",
    instructions:
      task.type === "CUSTOMER_DELIVERY"
        ? "Delivery instructions"
        : "Address instructions",
    contactName: "Contact name",
    contactPhone: "Contact phone",
    contactEmail: "Contact email",
  } as const;

  return (
    <Card padding={4} {...stylex.props(held ? styles.heldCard : undefined)}>
      <div {...stylex.props(styles.cardTop)}>
        <div {...stylex.props(styles.cardLeft)}>
          <div {...stylex.props(styles.shipmentReference)}>
            {shipmentReference}
          </div>
          <Text size="sm" color="secondary">
            {taskReference} · {taskType}
          </Text>
          <div {...stylex.props(styles.seqRow)}>
            <span {...stylex.props(styles.name)}>
              {currentName ?? task.type}
            </span>
          </div>
          <div {...stylex.props(styles.contactRow)}>
            <Text size="sm" color="secondary">
              {contactPhone || "No contact phone"}
            </Text>
            {dialNumber ? (
              <a
                href={`tel:${dialNumber}`}
                aria-label={`Call ${currentName || "contact"} at ${contactPhone}`}
                {...stylex.props(styles.callLink)}
              >
                <Phone size={14} aria-hidden="true" />
                Call
              </a>
            ) : null}
          </div>
          <div {...stylex.props(styles.longText)}>
            <Text size="sm" color="secondary">
              {addressText(task) || "No address"}
            </Text>
          </div>
          {task.type === "CUSTOMER_DELIVERY" && (
            <div {...stylex.props(styles.senderRow)}>
              <div {...stylex.props(styles.senderLabel)}>
                <Text size="sm" color="secondary">
                  Shipment sender:
                </Text>
              </div>
              <div {...stylex.props(styles.senderValue)}>
                <Text size="sm">{senderDisplay}</Text>
              </div>
            </div>
          )}
          {taskInstructions &&
            !operationalUpdate?.changedFields.includes("instructions") && (
              <div {...stylex.props(styles.longText)}>
                <Text size="sm">
                  {task.type === "CUSTOMER_DELIVERY"
                    ? "Delivery instructions"
                    : "Address instructions"}
                  : {taskInstructions}
                </Text>
              </div>
            )}
          <div {...stylex.props(styles.taskStatus)}>
            <Badge
              variant={
                held
                  ? "neutral"
                  : (TASK_STATUS_VARIANT[task.status] ?? "neutral")
              }
              label={held ? "Cancellation review" : task.status}
            />
          </div>
        </div>
        <a
          href={mapsHref(task)}
          target="_blank"
          rel="noreferrer"
          {...stylex.props(styles.mapLink)}
        >
          <MapPin size={14} />
          Map
          <ExternalLink size={12} />
        </a>
      </div>

      {operationalUpdate && operationalUpdate.changedFields.length > 0 ? (
        <div role="status" {...stylex.props(styles.operationalNotice)}>
          <Text size="sm" weight="semibold">
            Delivery details updated after assignment
          </Text>
          <dl {...stylex.props(styles.operationalRows)}>
            {operationalUpdate.changedFields.map((field) => (
              <div key={field} {...stylex.props(styles.operationalField)}>
                <dt>
                  <Text size="sm" weight="semibold">
                    {operationalLabels[field]}
                  </Text>
                </dt>
                <dd {...stylex.props(styles.operationalValues)}>
                  <p {...stylex.props(styles.operationalValue)}>
                    <Text size="sm" color="secondary">
                      {`Previously: ${
                        (field === "unit"
                          ? task.addressLine2
                          : field === "instructions"
                            ? taskInstructions
                            : task[field]) || "None"
                      }`}
                    </Text>
                  </p>
                  <p {...stylex.props(styles.operationalValue)}>
                    <Text size="sm">
                      {`Current: ${operationalUpdate.current[field] || "Removed"}`}
                    </Text>
                  </p>
                </dd>
              </div>
            ))}
          </dl>
          <div {...stylex.props(styles.reviewStatus)}>
            {operationalUpdate.acknowledged ? (
              <Text size="sm" color="secondary">
                Reviewed
              </Text>
            ) : (
              <Button
                size="sm"
                variant="primary"
                label={
                  acknowledgeUpdate.isPending
                    ? "Saving…"
                    : "I reviewed this update"
                }
                isDisabled={acknowledgeUpdate.isPending}
                onClick={() => acknowledgeUpdate.mutate()}
              />
            )}
          </div>
        </div>
      ) : null}

      {held ? (
        <div role="status" {...stylex.props(styles.holdNotice)}>
          <PauseCircle size={18} aria-hidden="true" />
          <div {...stylex.props(styles.holdText)}>
            <Text size="sm" weight="semibold">
              Completion on hold
            </Text>
            <Text size="sm" color="secondary">
              {cancellationReviewCopy(task.status)}
            </Text>
          </div>
        </div>
      ) : null}

      {done ? null : (
        <div {...stylex.props(styles.actions)}>
          {needsDeliveryStart ? (
            <Button
              size="sm"
              variant="primary"
              width="100%"
              label="Start delivery"
              isDisabled={
                held || tripStatus !== "ACTIVE" || startDelivery.isPending
              }
              isLoading={startDelivery.isPending}
              onClick={() => startDelivery.mutate()}
            />
          ) : (
            <Button
              size="sm"
              variant="primary"
              width="100%"
              label={held ? "Completion on hold" : "Complete"}
              isDisabled={held}
              onClick={() => {
                setCompletionError(null);
                setCompleteOpen(true);
              }}
            />
          )}
          <Button
            size="sm"
            variant="destructive"
            width="100%"
            label="Fail"
            onClick={() => setFailOpen(true)}
          />
        </div>
      )}

      {/* COMPLETE-WITH-PROOF dialog */}
      <Dialog isOpen={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogHeader title="Complete task" />
        <div {...stylex.props(styles.dialogBody)}>
          {actionIdentity}
          <Text size="sm" color="secondary">
            Attach a proof photo for {task.contactName ?? task.type}.
          </Text>
          {completionError || held ? (
            <div role="alert" {...stylex.props(styles.errorNotice)}>
              <Text size="sm">
                {completionError ?? cancellationReviewCopy(task.status)}
              </Text>
            </div>
          ) : null}
          <div {...stylex.props(styles.photoSection)}>
            {previewUrl ? (
              <div {...stylex.props(styles.preview)}>
                <img
                  src={previewUrl}
                  alt="Proof preview"
                  {...stylex.props(styles.previewImg)}
                />
                <button
                  type="button"
                  onClick={() => setProofFile(null)}
                  aria-label="Remove photo"
                  {...stylex.props(styles.removeBtn)}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <Button
                variant="secondary"
                width="100%"
                icon={<Camera size={16} />}
                label="Take / choose photo"
                onClick={() => fileInputRef.current?.click()}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setProofFile(e.target.files?.[0] ?? null)}
              {...stylex.props(styles.hiddenInput)}
            />
          </div>

          <TextInput
            label="Notes (optional)"
            value={notes}
            onChange={(value) => setNotes(value)}
            placeholder="e.g. Left with front desk"
          />
        </div>
        <div {...stylex.props(styles.dialogFooter)}>
          <Button
            variant="ghost"
            label="Cancel"
            onClick={() => setCompleteOpen(false)}
            isDisabled={completeTask.isPending}
          />
          <Button
            variant="primary"
            label="Complete task"
            isDisabled={!proofFile || held || needsDeliveryStart}
            isLoading={completeTask.isPending}
            onClick={() => completeTask.mutate()}
          />
        </div>
      </Dialog>

      {/* FAIL dialog */}
      <Dialog isOpen={failOpen} onOpenChange={setFailOpen}>
        <DialogHeader title="Fail task" />
        <div {...stylex.props(styles.dialogBody)}>
          {actionIdentity}
          <Text size="sm" color="secondary">
            Tell us what went wrong with {task.contactName ?? task.type}.
          </Text>
          <TextArea
            label="Reason for failure"
            isRequired
            value={reason}
            onChange={(value) => setReason(value)}
            rows={3}
            placeholder="e.g. Customer not home, no safe drop-off"
          />
        </div>
        <div {...stylex.props(styles.dialogFooter)}>
          <Button
            variant="ghost"
            label="Cancel"
            onClick={() => setFailOpen(false)}
            isDisabled={failTask.isPending}
          />
          <Button
            variant="destructive"
            label="Mark failed"
            isDisabled={!reason.trim()}
            isLoading={failTask.isPending}
            onClick={() => failTask.mutate()}
          />
        </div>
      </Dialog>
    </Card>
  );
}
